// src/utils/mobileConsole.ts
type Level = 'log' | 'info' | 'warn' | 'error';
let overlay: HTMLDivElement | null = null;
let list: HTMLDivElement | null = null;
let enabled = false;
let maxItems = 300;

// Estilos inline para no depender de CSS externo
const baseCss = `
  position:fixed; left:0; right:0; bottom:0; max-height:45vh; z-index:99999;
  background:rgba(18,18,18,.95); color:#e6e6e6; font:12px/1.4 ui-monospace, SFMono-Regular, Menlo, monospace;
  border-top:1px solid rgba(255,255,255,.15); box-shadow: 0 -4px 20px rgba(0,0,0,.3);
`;
const headerCss = `
  display:flex; gap:8px; align-items:center; padding:6px 8px; border-bottom:1px solid rgba(255,255,255,.1);
`;
const btnCss = `
  background:#2b2b2b; color:#ddd; border:1px solid #444; border-radius:6px; padding:2px 8px; cursor:pointer;
`;
const listCss = `
  overflow:auto; padding:6px 8px; max-height:34vh; word-break:break-word; white-space:pre-wrap;
`;

function fmt(v: any): string {
	try {
		if (typeof v === 'string') return v;
		return JSON.stringify(v, null, 2);
	} catch {
		return String(v);
	}
}
function nowTime(): string {
	const d = new Date();
	const p = (n: number) => String(n).padStart(2, '0');
	return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function addRow(level: Level, args: any[]) {
	if (!list) return;
	const row = document.createElement('div');
	const tag = level.toUpperCase().padEnd(5, ' ');
	const color = level === 'error' ? '#ff6b6b' : level === 'warn' ? '#ffd166' : '#6ee7ff';
	row.style.margin = '2px 0';
	row.innerHTML = `<span style="color:${color}">[${tag}]</span> <span style="opacity:.7">${nowTime()}</span> — ` + args.map(a => fmt(a)).join(' ');
	list.appendChild(row);
	// trim buffer
	while (list.children.length > maxItems) list.removeChild(list.firstChild as Node);
	list.scrollTop = list.scrollHeight;
}

function installOverlay() {
	if (overlay) return;
	overlay = document.createElement('div');
	overlay.setAttribute('data-mobile-console', '1');
	overlay.setAttribute('style', baseCss);

	const header = document.createElement('div');
	header.setAttribute('style', headerCss);
	header.innerHTML = `
    <strong style="flex:1">🛠 debug console</strong>
    <button data-act="hide" style="${btnCss}">Ocultar</button>
    <button data-act="clear" style="${btnCss}">Limpiar</button>
  `;

	list = document.createElement('div');
	list.setAttribute('style', listCss);

	overlay.appendChild(header);
	overlay.appendChild(list);
	document.body.appendChild(overlay);

	header.addEventListener('click', (e: any) => {
		const act = e && e.target && typeof e.target.getAttribute === 'function' ? e.target.getAttribute('data-act') : null;
		if (act === 'hide') overlay!.style.display = 'none';
		if (act === 'clear') list!.innerHTML = '';
	});

	// Info de dispositivo al abrir
	addRow('info', [
		'UA:',
		navigator.userAgent,
		'\nmediaDevices:',
		!!(navigator.mediaDevices && (navigator.mediaDevices as any).getUserMedia),
		'\nmaxTouchPoints:',
		(navigator as any).maxTouchPoints || 0,
	]);
}

export function enableOverlayConsole(opts?: { max?: number }) {
	if (enabled) return;
	enabled = true;
	if (opts && typeof opts.max === 'number') maxItems = opts.max!;
	installOverlay();

	// Mirror de console.*
	const original: Record<Level, any> = {
		log: console.log.bind(console),
		info: console.info.bind(console),
		warn: console.warn.bind(console),
		error: console.error.bind(console),
	};
	(['log', 'info', 'warn', 'error'] as Level[]).forEach(lvl => {
		(console as any)[lvl] = (...args: any[]) => {
			try {
				addRow(lvl, args);
			} catch {}
			try {
				original[lvl](...args);
			} catch {}
		};
	});

	// Errores globales
	window.addEventListener('error', (ev: ErrorEvent) => {
		addRow('error', ['window.onerror:', ev && ev.message ? ev.message : ev]);
	});
	window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const reason: any = (ev as any).reason;
		addRow('error', ['unhandledrejection:', reason && typeof reason === 'object' && 'message' in reason ? reason.message : reason]);
	});
}

export function maybeEnableConsole() {
	try {
		const q = new URLSearchParams(location.search);
		if (q.get('debug') === '1') localStorage.setItem('debug_console', '1');
		if (localStorage.getItem('debug_console') === '1') enableOverlayConsole();
	} catch {}
}

// Atajo para loguear desde tu código sin romper si no está habilitado
export function debugLog(level: Level, ...args: any[]) {
	(console as any)[level](...args);
}
